import { Box, CircularProgress } from '@mui/material';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { NoAccess } from '../components/states';
import { useAuth } from './AuthProvider';
import type { Capability } from './capabilities';

export const MFA_SETUP_PATH = '/setup-mfa';

export function Splash() {
  return (
    <Box
      sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}
      role="progressbar"
      aria-label="Loading"
    >
      <CircularProgress size={28} />
    </Box>
  );
}

/** Signed-in screens. While MFA setup is required, everything redirects to the setup flow. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, mfaSetupRequired } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Splash />;
  if (status === 'signedOut')
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (mfaSetupRequired && location.pathname !== MFA_SETUP_PATH)
    return <Navigate to={MFA_SETUP_PATH} replace />;
  return children;
}

export function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'signedIn') return <Navigate to="/" replace />;
  return children;
}

/** Wraps a screen that needs capabilities; shows the no-access state otherwise. */
export function RequireCapability({
  anyOf,
  what,
  children,
}: {
  anyOf: Capability[];
  what?: string;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!anyOf.some((c) => can(c))) return <NoAccess what={what} />;
  return children;
}
